import * as React from 'react';
import { LocalizationService } from '../../../services/LocalizationService';
import { IAMessageVideo } from '../video/MessageVideo';
import { IAIcon, IMessage } from '../../..';
import { makeStyles } from 'office-ui-fabric-react';
import { keyframes } from '@uifabric/merge-styles';
import { ILike } from '../../../interfaces/ILike';
import { IUser } from '../../../interfaces/IUser';
import { IAMessageCategories } from '../../viewer/categories/MessageCategories';
import { IFontSlots } from '../../../interfaces/IFontSlots';
import { ReelsPopup } from './ReelsPopup';
import { Helper } from '../../../Helper';
import MessageService from '../../../services/MessageService';
import IAAuthors from '../../panels/authors/IAAuthors';
import { ENTER_KEY, SPACEBAR_KEY } from '../../../global/Constants';

export interface IReelsViewProps {
  messages: IMessage[];
  environment: "Production" | "Development";
  tenant: string;
  token: string;
  highlightColor: string;
  rollupHighlightColor: string;
  user: IUser;
  type: string;
  instanceName?: string;
  showAuthor?: boolean;
  source?: "Home" | "Mobile" | "SharePoint";
  showEmojiPickerExpandButton?: boolean;
  likesUpdated: (likes: ILike[]) => void;
  isMobile?: boolean;
  reactionOptions?: any[];
  fontSlots?: IFontSlots;
  likesEnabled?: boolean;
  showUniqueViews?: boolean;
  showTitle?: "show" | "onHover" | "hide";
  borderRadius?: number;
  backgroundColor?: string;
  textColor?: string;
  showDropShadow?: boolean;
  borderColor?: string;
  showReelsInOneLine?: boolean;
  pageNumber?: number;
  numMessages?: number;
  isLoading?: boolean;
  showCategories?: boolean;
  enableReadUnreadMessage?: boolean;
  highlightUnread?: string;
  onEditMessage?: (message: IMessage) => void;
}

const skeletonPulse = keyframes({
  from: { opacity: 1 },
  to: { opacity: 0.4 },
});

const useClasses = makeStyles({
  reels: {
    display: 'flex',
    overflowX: 'hidden',
    padding: '1rem 0px',
    flexDirection: 'row',
    position: 'relative',
    boxSizing: 'border-box',
  },
  reelsContainer: {
    display: 'flex',
    gap: '1rem',
    width: 'auto',
    flexDirection: 'row',
    transition: 'transform 0.3s ease',
  },
  reel: {
    backgroundColor: '#000000',
    overflow: 'hidden',
    width: '250px',
    height: '444px',
    flexShrink: 0,
    position: 'relative',
    display: 'inline-block',
    cursor: 'pointer',
  },
  video: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  overlay: {
    backgroundColor: "transparent",
    position: 'absolute',
    bottom: 0,
    width: '100%',
    height: '100%'
  },
  title: {
    position: 'absolute',
    height: '25%',
    bottom: 0,
    width: '100%',
    padding: '0.5rem',
    color: '#ffffff',
    fontSize: '1rem',
    fontWeight: 'bold',
    textAlign: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.5s ease',
  },
  categories: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    padding: '0.5rem',
    boxSizing: 'border-box',
  },
  navButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.9)',
    border: 'none',
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
  },
  // Popup styles moved to ReelsPopup.tsx
  navButtonPrev: {
    left: '10px',
    transition: 'transform 0.2s ease',
  },
  navButtonNext: {
    right: '10px',
    transition: 'transform 0.2s ease',
  },
  socialFunctions: {
    marginTop: '15px',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '15px',
    borderTop: '1px solid #e0e0e0',
  },
  skeletonReel: {
    backgroundColor: '#e0e0e0',
    overflow: 'hidden',
    width: '100%',
    height: '444px',
    flexShrink: 0,
    position: 'relative',
    borderRadius: '4px',
    animationName: skeletonPulse,
    animationDuration: '1.2s',
    animationIterationCount: 'infinite',
    animationDirection: 'alternate',
    animationTimingFunction: 'ease-in-out',
  },
});

export function ReelsView(props: IReelsViewProps): JSX.Element {
  const styles = useClasses();
  const localizationService: LocalizationService = new LocalizationService();
  // On mobile, reels always render full width in a single column with "load more"
  // paging (like card view), regardless of the showReelsInOneLine setting.
  const showOneLine = props.showReelsInOneLine && !props.isMobile;
  const showAllMessages = showOneLine || props.isMobile;
  const reelsRef = React.useRef<HTMLDivElement>(null);
  const [offset, setOffset] = React.useState(0);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const [prevHovered, setPrevHovered] = React.useState(false);
  const [nextHovered, setNextHovered] = React.useState(false);
  const [activeMessage, setActiveMessage] = React.useState<IMessage | null>(null);
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  const [pageLoading, setPageLoading] = React.useState(false);
  const prevPageNumber = React.useRef<number | undefined>(props.pageNumber);
  const [containerWidth, setContainerWidth] = React.useState(0);

  const ITEM_WIDTH = 266; // 250px + 16px gap
  const REEL_WIDTH = 250;

  const updateScrollState = React.useCallback((currentOffset: number) => {
    const el = reelsRef.current;
    if (!el) return;
    const totalWidth = el.scrollWidth;
    const visibleWidth = el.clientWidth;
    setCanScrollLeft(currentOffset > 0);
    setCanScrollRight(currentOffset + visibleWidth < totalWidth);
    setContainerWidth(visibleWidth);
  }, []);

  React.useEffect(() => {
    localizationService?.checkLocalizedStrings().then(() => { });
  }, []);

  React.useEffect(() => {
    // Mobile shows the full accumulated message list (like card view's "load more"),
    // so there's no per-page slice whose emptiness needs a skeleton swap.
    if (showAllMessages) return;
    if (prevPageNumber.current !== props.pageNumber) {
      prevPageNumber.current = props.pageNumber;
      const pageNum = props.pageNumber ?? 1;
      const numMsgs = props.numMessages ?? 0;
      const slice = numMsgs > 0
        ? props.messages?.slice((pageNum - 1) * numMsgs, pageNum * numMsgs)
        : props.messages;
      if (!slice || slice.length === 0) {
        setPageLoading(true);
      }
    }
  }, [props.pageNumber]);

  React.useEffect(() => {
    if (showAllMessages) return;
    if (pageLoading) {
      const pageNum = props.pageNumber ?? 1;
      const numMsgs = props.numMessages ?? 0;
      const slice = numMsgs > 0
        ? props.messages?.slice((pageNum - 1) * numMsgs, pageNum * numMsgs)
        : props.messages;
      if (slice && slice.length > 0) {
        setPageLoading(false);
      }
    }
  }, [props.messages, props.pageNumber, pageLoading]);

  React.useEffect(() => {
    setOffset(0);
    updateScrollState(0);
    if (activeMessage) {
      const updated = props.messages?.find(m => m.id === activeMessage.id);
      if (updated) {
        setActiveMessage(updated);
      }
    }
  }, [props.messages]);

  React.useEffect(() => {
    updateScrollState(offset);
  }, [offset]);

  const handlePrev = () => {
    const next = Math.max(0, offset - ITEM_WIDTH);
    setOffset(next);
  };

  const handleNext = () => {
    const el = reelsRef.current;
    if (!el) return;
    const maxOffset = el.scrollWidth - el.clientWidth;
    const next = Math.min(maxOffset, offset + ITEM_WIDTH);
    setOffset(next);
  };


  return (
    <div style={{ position: 'relative' }}>
      <div className={styles.reels} ref={reelsRef}>
        <div
          className={styles.reelsContainer}
          style={{
            transform: showOneLine ? `translateX(-${offset}px)` : undefined,
            display: showOneLine ? 'flex' : 'grid',
            gridTemplateColumns: showOneLine ? undefined : (props.isMobile ? '1fr' : 'repeat(auto-fill, minmax(250px, 1fr))'),
            width: showOneLine ? 'fit-content' : '100%',
            flexWrap: showOneLine ? 'nowrap' : undefined,
          }}
        >
          {(!showOneLine && (props.isLoading || pageLoading))
            ? Array.from({ length: props.numMessages ?? 6 }).map((_, index) => (
              <div key={index} className={styles.skeletonReel} style={{ height: 'auto', aspectRatio: '250 / 444' }} />
            ))
            : (showAllMessages
              ? props.messages
              : props.messages?.slice(
                ((props.pageNumber ?? 1) - 1) * (props.numMessages ?? props.messages?.length ?? 0),
                (props.pageNumber ?? 1) * (props.numMessages ?? props.messages?.length ?? 0)
              )
            )?.map((message, index) => {
              const itemLeft = index * ITEM_WIDTH;
              const itemRight = itemLeft + REEL_WIDTH;
              const isReelVisible = !showOneLine || (itemRight > offset && itemLeft < offset + containerWidth);
              let videoUrl = message?.videoUrl;
              if ((location.href.includes("sharepoint.com") && message?.videoEmbedCode) || (videoUrl == undefined && message?.videoEmbedCode)) {
                videoUrl = Helper.getVideoSrcFromEmbedCodeWithNoAutoplay(message.videoEmbedCode);
              }
              return (
                <div
                  key={index}
                  className={styles.reel}
                  tabIndex={isReelVisible ? 0 : -1}
                  onContextMenu={(e) => e.preventDefault()}
                  onClick={() => {
                    if (!message.isRead) {
                      MessageService.readMessage(props.environment, props.token, props.tenant, props.instanceName ?? "", message.id);
                      message.isRead = true;
                    }
                    setActiveMessage(message);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === ENTER_KEY || event.key === SPACEBAR_KEY) {
                      event.preventDefault();
                      event.stopPropagation();
                      if (!message.isRead) {
                        MessageService.readMessage(props.environment, props.token, props.tenant, props.instanceName ?? "", message.id);
                        message.isRead = true;
                      }
                      setActiveMessage(message);
                      event.currentTarget.blur();
                    }
                  }}
                  onMouseEnter={props.showTitle === "onHover" ? () => setHoveredIndex(index) : undefined}
                  onMouseLeave={props.showTitle === "onHover" ? () => setHoveredIndex(null) : undefined}
                  style={{
                    borderRadius: props.borderRadius ? props.borderRadius : 'none',
                    boxShadow: props.showDropShadow ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
                    border: props.borderColor && props.borderColor !== "transparent" ? `1px solid ${props.borderColor}` : 'none',
                    width: showOneLine ? undefined : '100%',
                    height: showOneLine ? undefined : 'auto',
                    aspectRatio: showOneLine ? undefined : '250 / 444',
                    borderLeft: !message.isRead && props.enableReadUnreadMessage ? `4px solid ${props.highlightUnread ?? 'red'}` : (props.borderColor && props.borderColor !== "transparent" ? props.borderColor : 'none'),
                  }}
                >
                  <div className={styles.video}>
                    {(message.videoUrl || message?.videoEmbedCode) &&
                      <IAMessageVideo
                        videoUrl={videoUrl}
                        title={message.title}
                        width={"100%"}
                        height={"100%"}
                        environment={props.environment}
                        tenant={props.tenant}
                        token={props.token}
                        showControls={false}
                        backgroundColor={props.backgroundColor}
                      />
                    }
                  </div>
                  <div className={styles.overlay}>
                    {props.showAuthor &&
                      <div className={styles.categories}>
                        <IAAuthors
                          highlightColor={props.highlightColor}
                          authors={message.contacts ?? []}
                        />
                      </div>
                    }
                    {props.showCategories && message.categories && message.categories?.length > 0 && (
                      <div
                        className={styles.categories}
                        style={{
                          top: props.showAuthor ? 41 : 0,
                        }}
                      >
                        <IAMessageCategories
                          categories={message.categories}
                          showOverflowCounter
                          disablePopup={props.isMobile}
                          fontSlots={props.fontSlots}
                          textColorWhenTransparentBackground={"#000000"}
                        />
                      </div>

                    )}
                    {props.showTitle !== "hide" &&
                      <div
                        className={styles.title}
                        style={{
                          opacity: props.showTitle === "onHover" ? (hoveredIndex === index ? 1 : 0) : 1,
                          fontFamily: props.fontSlots?.heading?.fontFamily,
                          backgroundColor: props.backgroundColor ?? 'rgba(0, 0, 0, 0.7)',
                          color: props.textColor ?? "#ffffff"
                        }}
                      >
                        <span style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}>{message.title}</span>
                      </div>
                    }
                  </div>
                </div>
              );
            })}
        </div>
      </div>
      {canScrollLeft && showOneLine && (
        <button
          className={`${styles.navButton} ${styles.navButtonPrev}`}
          style={{ transform: prevHovered ? 'translateY(-50%) scale(1.2)' : 'translateY(-50%)' }}
          onClick={handlePrev}
          onMouseEnter={() => setPrevHovered(true)}
          onMouseLeave={() => setPrevHovered(false)}
        >
          <IAIcon
            url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Left/SVG/ic_fluent_chevron_left_48_regular.svg"
            color={props.highlightColor}
            size={18}
            style={{
              marginTop: "3px"
            }}
          />
        </button>
      )}
      {canScrollRight && showOneLine && (
        <button
          className={`${styles.navButton} ${styles.navButtonNext}`}
          style={{ transform: nextHovered ? 'translateY(-50%) scale(1.2)' : 'translateY(-50%)' }}
          onClick={handleNext}
          onMouseEnter={() => setNextHovered(true)}
          onMouseLeave={() => setNextHovered(false)}
        >
          <IAIcon
            url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Right/SVG/ic_fluent_chevron_right_48_regular.svg"
            color={props.highlightColor}
            size={18}
            style={{
              marginTop: "3px"
            }}
          />
        </button>
      )}
      {activeMessage && (
        <ReelsPopup
          message={activeMessage}
          environment={props.environment}
          tenant={props.tenant}
          token={props.token}
          highlightColor={props.highlightColor}
          rollupHighlightColor={props.rollupHighlightColor}
          user={props.user}
          type={props.type}
          instanceName={props.instanceName}
          source={props.source}
          isMobile={props.isMobile}
          fontSlots={props.fontSlots}
          likesEnabled={props.likesEnabled}
          showUniqueViews={props.showUniqueViews}
          showCategories={props.showCategories}
          showEmojiPickerExpandButton={props.showEmojiPickerExpandButton}
          reactionOptions={props.reactionOptions}
          likesUpdated={props.likesUpdated}
          onClose={() => setActiveMessage(null)}
          onShowNext={(() => {
            if (!props.messages || props.messages.length === 0) {
              return undefined;
            }
            const currentIndex = props.messages.findIndex(m => m.id === activeMessage.id);
            const nextIndex = (currentIndex + 1) % props.messages.length;
            if (currentIndex === nextIndex) {
              return undefined;
            }
            return () => setActiveMessage(props.messages[nextIndex]);
          })()}
          onEdit={props.onEditMessage ? () => {
            props.onEditMessage?.(activeMessage);
          } : undefined}
        />
      )}
    </div >
  );
}